import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import PublicLayout from "@/components/PublicLayout";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { readApiError } from "@/lib/format";

// Phase 1.6 imagery
const COMMUNITY_HERO =
  "https://static.prod-images.emergentagent.com/jobs/3112c7fc-8e98-4a3a-b5ca-162acb16129b/images/5368e357635f30a89b0ae2c638857fd61d1f79203c48bcee5dbe065d0551e8f9.png";
const BRASS_TOOLS =
  "https://static.prod-images.emergentagent.com/jobs/3112c7fc-8e98-4a3a-b5ca-162acb16129b/images/d27e3052e8d2bd90c3ebefbee5063da2acb35a7738470555223faa91852d370f.png";

const VALUE_BULLETS = [
  "Two live workshops a month with Kenny on Zoom — bonsai shaping, terrarium building, succulent care.",
  "Member-only cuttings exchange — trade your propagations with the community.",
  "Kenny's full pruning + wiring video library — 40+ technique videos and growing.",
  "First access to limited-edition trees and tools before they're listed publicly.",
];

const PERKS = [
  {
    title: "Live monthly workshops",
    body: "Wiring a juniper. Repotting a baby black pine. Building a closed-glass terrarium. On camera, your questions in chat, recorded so you can come back to them.",
  },
  {
    title: "Member-only cuttings & seed exchange",
    body: "Every member sends in something they're growing. Every member gets something back from the coast. It's the slowest, most generous Discord you'll ever join.",
  },
  {
    title: "The full pruning library",
    body: "Every technique I've worked out in ten years — written down, with photos, with the mistakes I made the first time. Searchable. Yours for as long as you're in.",
  },
  {
    title: "Direct access to me",
    body: "Stuck? Send a photo to the channel. I answer everything in 24 hours.",
  },
];

export default function Community() {
  const [loading, setLoading] = useState(false);

  const join = async () => {
    setLoading(true);
    try {
      const res = await api.post(`/checkout/membership`, {
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error("Could not open checkout", { description: readApiError(e) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicLayout overlayNav padTop={false}>
      {/* HERO — community workshop image */}
      <section
        data-testid="community-hero"
        className="relative isolate flex min-h-[78vh] items-center overflow-hidden"
      >
        <div className="absolute inset-0 z-0">
          <img
            src={COMMUNITY_HERO}
            alt="A Coastal Roots community workshop"
            className="h-full w-full object-cover"
            style={{ filter: "brightness(1.35) contrast(1.05) saturate(1.08)" }}
          />
          <div className="absolute inset-y-0 left-0 w-3/5 bg-gradient-to-r from-[#3D3A33]/80 via-[#3D3A33]/45 to-transparent" />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-[#3D3A33]" />
        </div>
        <div className="relative z-10 mx-auto w-full max-w-5xl px-6 pt-32 pb-20 text-[#F5EFE2]">
          <p className="text-xs uppercase tracking-[0.32em] text-[#C9A961]">
            The Coastal Roots Community · Skool
          </p>
          <h1
            data-testid="community-headline"
            className="mt-3 font-serif text-5xl leading-[1.05] tracking-wide sm:text-6xl lg:text-7xl"
          >
            Find your people.
            <br />
            Grow something real together.
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-[#F5EFE2]/90 sm:text-lg">
            Fifty dollars a month. Live workshops with me, the full pruning
            library, a cuttings exchange between members, and a Discord-quiet
            community of people who&apos;d rather repot than scroll.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button
              data-testid="community-cta-join"
              onClick={join}
              disabled={loading}
              size="lg"
              className="bg-[#C9A961] text-[#3D3A33] hover:bg-[#DDBE7A]"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Opening Stripe…
                </>
              ) : (
                <>
                  Join for $50/month
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </Button>
            <a
              href="#perks"
              className="inline-flex items-center text-sm text-[#F5EFE2]/85 underline-offset-4 hover:text-[#C9A961] hover:underline"
            >
              See what&apos;s inside
            </a>
          </div>
        </div>
      </section>

      {/* WHAT YOU GET — brass tools image + 4 value bullets */}
      <section
        data-testid="community-value"
        className="border-y border-[#5E594E]/60 bg-[#3D3A33]"
      >
        <div className="mx-auto grid max-w-6xl gap-12 px-6 py-24 md:grid-cols-[1fr_1.1fr] md:items-center">
          <div className="overflow-hidden rounded-2xl border border-[#5E594E]">
            <img
              src={BRASS_TOOLS}
              alt="Brass pruning tools laid out on dark linen"
              className="aspect-[4/5] w-full object-cover"
              loading="lazy"
            />
          </div>
          <div className="space-y-6">
            <p className="text-xs uppercase tracking-[0.24em] text-[#C9A961]">
              Membership
            </p>
            <h2 className="font-serif text-4xl tracking-wide text-[#F5EFE2] sm:text-5xl">
              What you get for $50/month.
            </h2>
            <ul className="space-y-4 text-base leading-relaxed text-[#F5EFE2]/90">
              {VALUE_BULLETS.map((b, i) => (
                <li
                  key={b}
                  data-testid={`community-value-${i}`}
                  className="flex gap-4"
                >
                  <span className="mt-2 inline-block h-1.5 w-6 shrink-0 rounded-full bg-[#C9A961]" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <p className="pt-2 text-sm text-[#7A8B6F]">
              Cancel any time from your receipt email. No friction, no winback.
            </p>
          </div>
        </div>
      </section>

      {/* PERKS — long-form */}
      <section
        id="perks"
        data-testid="community-perks"
        className="bg-[#3D3A33]"
      >
        <div className="mx-auto max-w-5xl px-6 py-24">
          <div className="max-w-2xl space-y-3">
            <p className="text-xs uppercase tracking-[0.24em] text-[#C9A961]">
              What&apos;s Inside
            </p>
            <h2 className="font-serif text-4xl tracking-wide text-[#F5EFE2] sm:text-5xl">
              No funnels. No tripwires.
              <br />
              Just the work.
            </h2>
            <p className="text-base leading-relaxed text-[#C9BEA8]">
              I built the community I&apos;d want to be a member of. If it&apos;s
              not for you, cancel any time — no friction, no winback campaigns.
            </p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {PERKS.map((p, i) => (
              <div
                key={p.title}
                data-testid={`community-perk-${i}`}
                className="rounded-2xl border border-[#5E594E] bg-[#4D4940] p-6 transition-colors hover:border-[#C9A961]/50"
              >
                <p className="text-xs uppercase tracking-[0.18em] text-[#C9A961]">
                  0{i + 1}
                </p>
                <h3 className="mt-2 font-serif text-2xl tracking-wide text-[#F5EFE2]">
                  {p.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-[#C9BEA8]">
                  {p.body}
                </p>
              </div>
            ))}
          </div>

          <div
            data-testid="community-bottom-cta"
            className="mt-16 flex flex-col items-start justify-between gap-6 rounded-2xl border border-[#C9A961]/40 bg-[#4D4940] p-8 sm:flex-row sm:items-center"
          >
            <div className="max-w-md space-y-2">
              <p className="font-serif text-2xl tracking-wide text-[#F5EFE2]">
                Ready to put down roots?
              </p>
              <p className="text-sm text-[#C9BEA8]">
                Stripe-hosted, instant access. Cancel any time from your
                receipt email.
              </p>
            </div>
            <Button
              data-testid="community-bottom-join-btn"
              onClick={join}
              disabled={loading}
              size="lg"
              className="bg-[#C9A961] text-[#3D3A33] hover:bg-[#DDBE7A]"
            >
              {loading ? "Opening Stripe…" : "Join the community"}
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
